import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus } from 'lucide-react';

const UsersManagementModal = ({
  isOpen,
  onClose,
  onAddUser,
  onUpdateUser,
  onUpdatePermissions,
  onDeleteUser,
  onUpdatePhone,
  availablePages,
  roles = [],
  users = [],
  embedded = false
}) => {
  const navigate = useNavigate();
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [onlineFilter, setOnlineFilter] = useState('all');
  const [sortBy, setSortBy] = useState('username');
  const [sortOrder, setSortOrder] = useState('asc');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: '',
    phone: '',
    workshopLocation: '',
    permissions: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const workshopLocations = [
    'Мамбетова 325а',
    'Маметовой, 3а к5'
  ];

  // Получаем разрешения по умолчанию для роли
  const getDefaultPermissions = (role) => {
    const customRole = roles.find(r => r.roleName === role);
    if (customRole && customRole.permissions) {
      return customRole.permissions;
    }

    const defaultPerms = {
      'admin': Object.values(availablePages).flat().map(page => page.id),
      'adminb24': Object.values(availablePages).flat().map(page => page.id),
      'cutterb24': ['search-b24', 'cuttingb24'],
      'drillerb24': ['search-b24', 'drillingb24'],
      'zakalkab24': ['search-b24', 'zakalkab24'],
      'matovkab24': ['search-b24', 'matovkab24'],
      'scladb24': ['search-b24', 'scladb24'],
      'loftb24': ['search-b24', 'loftb24'],
      'upakb24': ['search-b24', 'upakb24'],
      'pickupb24': ['search-b24', 'pickupb24'],
      'sendb24': ['search-b24', 'sendb24'],
      'driverb24': ['search-b24', 'driverb24'],
      'workplaceb24': ['search-b24', 'loftb24'],
      'designer': ['search-b24', 'visualloft'],
      'franchise': ['search-b24', 'visualloft']
    };
    return defaultPerms[role] || ['search-b24'];
  };

  const handleRoleChange = (role) => {
    const defaultPermissions = getDefaultPermissions(role);
    setFormData({
      ...formData,
      role,
      permissions: defaultPermissions
    });
  };

  const handlePermissionChange = (permissionId) => {
    const updatedPermissions = formData.permissions.includes(permissionId)
      ? formData.permissions.filter(p => p !== permissionId)
      : [...formData.permissions, permissionId];

    setFormData({
      ...formData,
      permissions: updatedPermissions
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onAddUser(formData);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: '',
        phone: '',
        workshopLocation: '',
        permissions: []
      });
      setShowAddForm(false);
    } catch (error) {
      setError(error.message || 'Ошибка при добавлении пользователя');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: '',
      phone: '',
      permissions: []
    });
    setShowAddForm(false);
    setError('');
    onClose();
  };

  const getRoleDisplayName = (roleName) => {
    // Сначала ищем в ролях из БД
    const customRole = roles.find(r => r.roleName === roleName);
    if (customRole && customRole.displayName) {
      return customRole.displayName;
    }

    // Fallback на статический маппинг для совместимости
    const roleMap = {
      'cutterb24': 'Резчик',
      'driverb24': 'Водитель',
      'drillerb24': 'Сверловщик',
      'matovkab24': 'Матовка',
      'zakalkab24': 'Закалка',
      'scladb24': 'Склад',
      'upakb24': 'Упаковка',
      'sendb24': 'Отправка',
      'loftb24': 'Лофт/Зеркало сборка',
      'workplaceb24': 'Рабочее место',
      'adminb24': 'Администратор',
      'admin': 'Администратор',
      'designer': 'Дизайнер',
      'franchise': 'Франчайзи'
    };
    return roleMap[roleName] || roleName;
  };

  // Фильтрация и сортировка пользователей
  const filterAndSortUsers = () => {
    let filtered = [...users];

    // Поиск
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.phone && user.phone.includes(searchTerm)) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Фильтр по роли
    if (selectedRole) {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    // Фильтр по онлайн статусу
    if (onlineFilter === 'online') {
      filtered = filtered.filter(user => user.isOnline);
    } else if (onlineFilter === 'offline') {
      filtered = filtered.filter(user => !user.isOnline);
    }

    // Сортировка
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'lastSeen') {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      } else {
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const filteredUsers = filterAndSortUsers();
  const availableRoles = [...new Set(users.map(user => user.role))].sort();

  const handleOpenUserActions = (user) => {
    navigate(`/users/${user.id}/edit`);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={embedded ? 'w-full' : 'fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto'}>
        <div className={embedded
          ? 'bg-white w-full flex flex-col'
          : 'bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col my-auto'}>
            {/* Header */}
            {!embedded && <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 md:px-8 py-5 flex justify-between items-center">
              <h2 className={`${embedded ? 'text-xl text-gray-900' : 'text-3xl text-white'} font-bold flex items-center`}>
                <span className={`${embedded ? 'mr-2 text-2xl' : 'mr-3 text-4xl'}`}>👥</span>
                Управление пользователями
              </h2>
              <button
                onClick={handleClose}
                className="text-white hover:text-gray-200 text-3xl font-bold transition-colors"
              >
                ×
              </button>
            </div>}

            {/* Content */}
            <div className={`flex-1 ${embedded ? 'overflow-visible p-4' : 'overflow-y-auto p-8'}`}>
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                  {error}
                </div>
              )}

              {/* Controls */}
              <div className="mb-5 flex flex-wrap items-center gap-2.5">
                <button
                  onClick={() => navigate('/users/new')}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3.5 text-sm font-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <UserPlus className="h-4 w-4" />
                  Добавить
                </button>

                {/* Search */}
                <div className="relative min-w-[240px] flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Поиск по имени, email, телефону или роли"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-10 w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                {/* Role Filter */}
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  aria-label="Фильтр по роли"
                  className="h-10 min-w-[150px] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-600 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Все роли</option>
                  {availableRoles.map(role => (
                    <option key={role} value={role}>{getRoleDisplayName(role)}</option>
                  ))}
                </select>

                {/* Online Filter */}
                <select
                  value={onlineFilter}
                  onChange={(e) => setOnlineFilter(e.target.value)}
                  aria-label="Фильтр по статусу"
                  className="h-10 min-w-[125px] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-600 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="all">Все</option>
                  <option value="online">🟢 Онлайн</option>
                  <option value="offline">⚫ Оффлайн</option>
                </select>
              </div>

              {/* Add User Form */}
              {showAddForm && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Добавить нового пользователя</h3>
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column - Basic Info */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Имя пользователя *
                          </label>
                          <input
                            type="text"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Пароль *
                          </label>
                          <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Роль *
                          </label>
                          <select
                            value={formData.role}
                            onChange={(e) => handleRoleChange(e.target.value)}
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          >
                            <option value="">Выберите роль</option>
                            {roles.length > 0 ? (
                              roles.map(role => (
                                <option key={role.roleName} value={role.roleName}>
                                  {role.displayName || role.roleName}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>Загрузка ролей...</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Телефон
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Местоположение цеха
                          </label>
                          <select
                            value={formData.workshopLocation}
                            onChange={(e) => setFormData({ ...formData, workshopLocation: e.target.value })}
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Не выбрано</option>
                            {workshopLocations.map(location => (
                              <option key={location} value={location}>{location}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Right Column - Permissions */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Разрешения ({formData.permissions.length})
                        </label>
                        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-4">
                          {Object.entries(availablePages).map(([category, pages]) => (
                            <div key={category} className="mb-4">
                              <h5 className="font-semibold text-gray-600 mb-2">{category}</h5>
                              <div className="grid grid-cols-1 gap-2">
                                {pages.map(page => (
                                  <label key={page.id} className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={formData.permissions.includes(page.id)}
                                      onChange={() => handlePermissionChange(page.id)}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm">{page.icon} {page.name}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Отмена
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400"
                      >
                        {loading ? 'Добавление...' : 'Добавить'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Users Table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Найдено пользователей: {filteredUsers.length} из {users.length}
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Пользователь</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Контакты</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Роль</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Разрешения</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUsers.map(user => {
                        const userPermissions = user.permissions || getDefaultPermissions(user.role);
                        return (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className={`w-3 h-3 rounded-full mr-2 ${user.isOnline ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                                <span className="font-medium">{user.username}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div>{user.email || '-'}</div>
                              {user.phone && <div className="mt-0.5 text-xs text-gray-400">{user.phone}</div>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {getRoleDisplayName(user.role)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${user.isOnline ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {user.isOnline ? 'Онлайн' : 'Оффлайн'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-1">
                                {userPermissions.slice(0, 2).map(permission => {
                                  const page = Object.values(availablePages).flat().find(p => p.id === permission);
                                  return (
                                    <span key={permission} className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                      {page?.icon} {page?.name || permission}
                                    </span>
                                  );
                                })}
                                {userPermissions.length > 2 && (
                                  <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                                    +{userPermissions.length - 2}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleOpenUserActions(user)}
                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
                              >
                                ⚙️ Управление
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer */}
            {!embedded && <div className="bg-gray-50 px-8 py-4 flex justify-end border-t">
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Закрыть
              </button>
            </div>}
          </div>
      </div>

    </>
  );
};

export default UsersManagementModal;
